
import React from 'react';

const PrivacyPolicy: React.FC = () => {
    return (
        <div className="max-w-3xl mx-auto px-4 py-12 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <h1 className="text-4xl font-black text-gray-900 mb-2">Privacy Policy</h1>
            <p className="text-sm text-gray-400 font-medium mb-8">Last Updated: February 24, 2026</p>

            <div className="bg-white rounded-[2rem] p-8 md:p-12 border border-gray-100 shadow-xl space-y-8 text-gray-700 leading-relaxed font-medium">

                <section className="space-y-4">
                    <h2 className="text-2xl font-bold text-gray-900">1. Introduction</h2>
                    <p>
                        Welcome to Queenli ("we," "our," or "us"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our creator platform website (the "Platform"). By using the Platform, you agree to the collection and use of information in accordance with this policy.
                    </p>
                </section>

                <section className="space-y-4">
                    <h2 className="text-2xl font-bold text-gray-900">2. Information We Collect</h2>

                    <h3 className="text-lg font-bold text-gray-800">Information You Provide Directly:</h3>
                    <ul className="list-disc pl-5 space-y-2">
                        <li><strong>Application Information:</strong> Name, email address, phone number, country, city, gender, social media handles, content creation experience, and availability.</li>
                        <li><strong>Account Information:</strong> Email and password when you create a creator or admin account.</li>
                        <li><strong>Content Submissions:</strong> Videos, images, and other media you upload through the Platform.</li>
                        <li><strong>Payment Information:</strong> Payment details necessary to process your earnings and payouts.</li>
                        <li><strong>Communications:</strong> Any messages or notes you provide through the Platform.</li>
                    </ul>

                    <h3 className="text-lg font-bold text-gray-800">Information Collected Automatically:</h3>
                    <ul className="list-disc pl-5 space-y-2">
                        <li><strong>Device Information:</strong> Browser type, operating system, and device identifiers.</li>
                        <li><strong>Usage Data:</strong> Pages visited, features used, session duration, and interactions with the Platform.</li>
                        <li><strong>Location Data:</strong> Approximate location based on IP address (used for country auto-detection during application).</li>
                    </ul>
                </section>

                <section className="space-y-4">
                    <h2 className="text-2xl font-bold text-gray-900">3. How We Use Your Information</h2>
                    <p>We use the information we collect to:</p>
                    <ul className="list-disc pl-5 space-y-2">
                        <li>Process and evaluate your creator application</li>
                        <li>Manage your creator account and dashboard access</li>
                        <li>Receive and manage your content submissions</li>
                        <li>Track and process your earnings and payouts</li>
                        <li>Communicate with you about your account, submissions, and program updates</li>
                        <li>Improve and optimize the Platform experience</li>
                        <li>Comply with legal obligations</li>
                    </ul>
                </section>

                <section className="space-y-4">
                    <h2 className="text-2xl font-bold text-gray-900">4. Data Storage and Security</h2>
                    <ul className="list-disc pl-5 space-y-2">
                        <li>Your data is stored securely using Supabase (cloud database) with row-level security policies.</li>
                        <li>Content uploads are stored securely via Frame.io and Supabase Storage.</li>
                        <li>We implement industry-standard security measures to protect your information.</li>
                        <li>Data transmission is encrypted using SSL/TLS protocols.</li>
                    </ul>
                </section>

                <section className="space-y-4">
                    <h2 className="text-2xl font-bold text-gray-900">5. Data Sharing and Disclosure</h2>
                    <p>We do not sell your personal information. We may share your information with:</p>
                    <ul className="list-disc pl-5 space-y-2">
                        <li><strong>Service Providers:</strong> Third-party services that help us operate the Platform (Supabase for database, Frame.io for content management, Resend for email delivery, Vercel for hosting).</li>
                        <li><strong>Content Distribution:</strong> Your submitted content may be published on official Queenli social media accounts as part of the creator program.</li>
                        <li><strong>Legal Requirements:</strong> When required by law, court order, or governmental authority.</li>
                        <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.</li>
                    </ul>
                </section>

                <section className="space-y-4">
                    <h2 className="text-2xl font-bold text-gray-900">6. Your Rights and Choices</h2>
                    <p>You have the right to:</p>
                    <ul className="list-disc pl-5 space-y-2">
                        <li><strong>Access:</strong> Request a copy of your personal data.</li>
                        <li><strong>Correction:</strong> Update or correct inaccurate information.</li>
                        <li><strong>Deletion:</strong> Request deletion of your personal data.</li>
                        <li><strong>Data Portability:</strong> Request your data in a portable format.</li>
                    </ul>
                    <p>
                        To exercise these rights, contact us at <a href="mailto:contact@torek.io" className="text-purple-600 font-bold hover:underline">contact@torek.io</a>.
                    </p>
                </section>

                <section className="space-y-4">
                    <h2 className="text-2xl font-bold text-gray-900">7. Data Retention</h2>
                    <p>
                        We retain your personal information for as long as your account is active or as needed to provide you services. Application data for rejected applicants is retained for up to 12 months. You may request deletion of your data at any time by contacting us.
                    </p>
                </section>

                <section className="space-y-4">
                    <h2 className="text-2xl font-bold text-gray-900">8. Children's Privacy</h2>
                    <p>
                        The Queenli creator platform is not intended for children under 18 years of age. We do not knowingly collect personal information from children under 18. If we discover we have collected information from a child under 18, we will delete it immediately.
                    </p>
                </section>

                <section className="space-y-4">
                    <h2 className="text-2xl font-bold text-gray-900">9. International Data Transfers</h2>
                    <p>
                        Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your data in accordance with this Privacy Policy.
                    </p>
                </section>

                <section className="space-y-4">
                    <h2 className="text-2xl font-bold text-gray-900">10. Changes to This Privacy Policy</h2>
                    <p>
                        We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
                    </p>
                </section>

                <section className="space-y-4">
                    <h2 className="text-2xl font-bold text-gray-900">11. Contact Us</h2>
                    <p>If you have questions about this Privacy Policy, please contact us at:</p>
                    <p>
                        Email: <a href="mailto:contact@torek.io" className="text-purple-600 font-bold hover:underline">contact@torek.io</a>
                    </p>
                </section>

                <div className="pt-8 border-t border-gray-100 text-center">
                    <p className="text-xs text-gray-400">
                        Queenli is a product of Torek, LLC.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;
